import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Merge, Pencil, Trash2, Check, X } from "lucide-react";

const inputCls = "w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm placeholder:text-zinc-400 focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20";

export const LeadSourcesPanel = () => {
  const [sources, setSources] = useState([]);
  const [selected, setSelected] = useState([]);
  const [target, setTarget] = useState("");
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/lead-sources");
    setSources(data);
    setSelected((sel) => sel.filter((n) => data.some((s) => s.name === n)));
  };
  useEffect(() => { load(); }, []);

  const toggle = (name) => {
    setSelected((sel) => {
      const next = sel.includes(name) ? sel.filter((n) => n !== name) : [...sel, name];
      if (next.length === 1 || !target) setTarget(next[0] || "");
      return next;
    });
  };

  const merge = async (names, into) => {
    setBusy(true);
    try {
      const { data } = await api.post("/lead-sources/merge", { sources: names, target: into });
      toast.success(`Saved as "${data.target}" · ${data.updated_projects} project(s) updated`);
      setSelected([]); setTarget(""); setRenaming(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (s) => {
    const msg = s.count ? `Delete "${s.name}"? ${s.count} project(s) will be set to Unknown.` : `Delete "${s.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await api.post("/lead-sources/delete", { name: s.name });
      toast.success("Source deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="lead-sources-panel" className="mt-8 rounded-xl border border-zinc-200 bg-white p-6">
      <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Dashboard pie chart</div>
      <h2 className="mt-1 text-lg font-semibold tracking-tight text-zinc-900">Manage Lead Sources</h2>
      <p className="mt-1 text-sm text-zinc-600">Tick two or more sources to merge them into one. Every project using them is updated.</p>

      {selected.length >= 2 && (
        <div data-testid="lead-sources-merge-bar" className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-[#0066ff]/30 bg-blue-50/60 p-3">
          <span className="text-sm font-medium text-zinc-800">Merge {selected.length} into</span>
          <input data-testid="lead-sources-merge-target" value={target} onChange={(e) => setTarget(e.target.value)} list="lead-sources-merge-list" className={`${inputCls} max-w-xs`} />
          <datalist id="lead-sources-merge-list">{selected.map((n) => <option key={n} value={n} />)}</datalist>
          <button data-testid="lead-sources-merge-btn" disabled={busy || !target.trim()} onClick={() => merge(selected, target)} className="inline-flex items-center gap-1.5 rounded-md bg-[#0066ff] px-3 py-2 text-sm font-medium text-white hover:bg-[#0052cc] disabled:opacity-50">
            <Merge size={14} /> Merge
          </button>
          <button data-testid="lead-sources-merge-clear" onClick={() => setSelected([])} className="rounded-md px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-100">Clear</button>
        </div>
      )}

      <div className="mt-4 divide-y divide-zinc-100 rounded-lg border border-zinc-200">
        {sources.map((s, i) => (
          <div key={s.name} data-testid={`lead-source-row-${i}`} className="flex items-center gap-3 px-3 py-2.5">
            <input type="checkbox" data-testid={`lead-source-check-${i}`} checked={selected.includes(s.name)} onChange={() => toggle(s.name)} className="h-4 w-4 rounded border-zinc-300 text-[#0066ff] focus:ring-[#0066ff]" />
            {renaming === s.name ? (
              <div className="flex flex-1 items-center gap-1.5">
                <input data-testid={`lead-source-rename-input-${i}`} autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && renameVal.trim()) merge([s.name], renameVal); if (e.key === "Escape") setRenaming(null); }}
                  className={inputCls} />
                <button data-testid={`lead-source-rename-save-${i}`} disabled={!renameVal.trim()} onClick={() => merge([s.name], renameVal)} className="rounded-md bg-[#0066ff] p-2 text-white hover:bg-[#0052cc] disabled:opacity-50"><Check size={14} /></button>
                <button onClick={() => setRenaming(null)} className="rounded-md border border-zinc-200 p-2 text-zinc-500 hover:bg-zinc-50"><X size={14} /></button>
              </div>
            ) : (
              <>
                <span className="flex-1 truncate text-sm text-zinc-900">{s.name}</span>
                <span data-testid={`lead-source-count-${i}`} className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-semibold text-zinc-600">{s.count} project{s.count === 1 ? "" : "s"}</span>
                <button data-testid={`lead-source-rename-${i}`} onClick={() => { setRenaming(s.name); setRenameVal(s.name); }} className="rounded-md p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700" title="Rename"><Pencil size={14} /></button>
                <button data-testid={`lead-source-delete-${i}`} onClick={() => remove(s)} className="rounded-md p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600" title="Delete"><Trash2 size={14} /></button>
              </>
            )}
          </div>
        ))}
        {sources.length === 0 && <div className="p-4 text-sm text-zinc-500">No lead sources yet.</div>}
      </div>
    </div>
  );
};
